import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

import PanelCard from '../../performance-operations/components/PanelCard';
import { KpiSub, KpiValue, Trend } from '../../performance-operations/styles';
import { fmt, hasNum, metricUnitSuffix, signed } from '../config';

// Analiz §14 E11 üst alan — karşılaştırılan dönemler, ortak çalışan n, final değişim M45, hedef değişimi M46 (yüzde puan; göreli
// değişim ayrı) ve yetkinlik değişimi M47. Değer yoksa neden metni (gizli / ölçek uyuşmazlığı / dönem kapanmadı / veri yok).
// Renk yönü: artış yeşil, düşüş kırmızı; renk her zaman ok ve sayıyla birlikte (erişilebilirlik).
function ChangeKpi({ code, title, change, loading, error, onRetry, mode }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  let body;
  if (!change || !hasNum(change.value)) {
    const reason = change?.suppressionReason ? 'suppressed' : (change?.unavailableReason || 'noData');
    body = (
      <>
        <KpiValue data-kpi={code}>{t('performance.operations.kpi.noData')}</KpiValue>
        <KpiSub data-reason={reason}>{t(`performance.trends.reason.${reason}`, { defaultValue: t('performance.trends.reason.noData') })}</KpiSub>
      </>
    );
  } else {
    const v = Number(change.value);
    const tone = v > 0 ? 'good' : v < 0 ? 'bad' : 'neutral';
    const Icon = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
    body = (
      <>
        <KpiValue data-kpi={code}>{`${signed(v, lang)} ${metricUnitSuffix(change, t)}`}</KpiValue>
        <KpiSub>
          {t('performance.trends.kpi.fromTo', { from: fmt(change.from, lang), to: fmt(change.to, lang) })}
          {change.basis === 'normalized' ? ` · ${t('performance.trends.kpi.normalizedLabel')}` : ''}
        </KpiSub>
        <Trend $tone={tone} data-trend={tone}>
          <Icon size={14} aria-hidden="true" />
          <span>
            {mode === 'population'
              ? t('performance.trends.kpi.populationN', { from: change.nFrom ?? 0, to: change.nTo ?? 0 })
              : t('performance.trends.kpi.cohortN', { n: change.n ?? 0 })}
          </span>
        </Trend>
        {hasNum(change.relativePct) ? <KpiSub data-relative="1">{t('performance.trends.kpi.relative', { value: signed(change.relativePct, lang) })}</KpiSub> : null}
      </>
    );
  }
  return (
    <PanelCard panelKey={`kpi-${code}`} title={title} span={2} kpi compact loading={loading} error={error} onRetry={onRetry}>
      {body}
    </PanelCard>
  );
}

export default function TrendKpis({ panel }) {
  const { t } = useTranslation();
  const s = panel.data;
  const common = { loading: panel.loading, error: panel.error, onRetry: panel.retry, mode: s?.mode };
  return (
    <>
      <PanelCard panelKey="kpi-periods" title={t('performance.trends.kpi.periods')} span={4} kpi compact
        loading={panel.loading} error={panel.error} onRetry={panel.retry}>
        <KpiValue data-kpi="periods">{s ? s.periods.length : ''}</KpiValue>
        <KpiSub>{s ? s.periods.map((p) => p.name).join(' → ') : ''}</KpiSub>
        {s ? (
          <KpiSub>
            {t(`performance.trends.mode.${s.mode}`)}
            {s.sameRole ? ` · ${t('performance.trends.control.sameRole')}` : ''}
          </KpiSub>
        ) : null}
      </PanelCard>
      <PanelCard panelKey="kpi-cohort" title={s?.mode === 'population' ? t('performance.trends.kpi.population') : t('performance.trends.kpi.cohort')}
        span={2} kpi compact loading={panel.loading} error={panel.error} onRetry={panel.retry}>
        <KpiValue data-kpi="cohort">{s ? (s.mode === 'population' ? s.scope.populationLast : s.cohortN) : ''}</KpiValue>
        <KpiSub>{s ? t('performance.trends.kpi.cohortSub', { first: s.scope.populationFirst, last: s.scope.populationLast }) : ''}</KpiSub>
      </PanelCard>
      <ChangeKpi code="M45" title={t('performance.trends.kpi.m45')} change={s?.m45} {...common} />
      <ChangeKpi code="M46" title={t('performance.trends.kpi.m46')} change={s?.m46} {...common} />
      <ChangeKpi code="M47" title={t('performance.trends.kpi.m47')} change={s?.m47} {...common} />
    </>
  );
}
